import { GroupCodesController } from '@/components/controls';
import { isImageUrl, isNominalId, isValidUrl } from '@/utils';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { Fragment, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

export function NoticeEditorDialog({ notice, isOpen, onClose, onDelete }) {
  const { control, handleSubmit, reset } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: notice,
  });

  useEffect(() => {
    reset(notice);
  }, [reset, notice, isOpen]);

  function handleClose() {
    onClose();
  }

  return (
    <form id="notice-editor-form" onSubmit={handleSubmit(onClose)}>
      <Dialog open={isOpen && !!notice} onClose={handleClose}>
        <DialogTitle>{`${notice.code ? 'Edit' : 'Add'} Notice`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText gutterBottom>
              Notices are for safety/information/logistical matters. Crime or
              intelligence information should be added elsewhere in the brief
              such as in Priority Intelligence.
            </DialogContentText>
            <DialogContentText>
              Please fill in the form below to add a notice. If groups are not
              specified then the notice will be visible to all.
            </DialogContentText>
            <GroupCodesController
              label="Groups"
              control={control}
              name="groupCodes"
              types={['SECTOR', 'TEAM']}
            />
            <Controller
              name="message"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Message"
                  multiline
                  rows={8}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name="nominalId"
              control={control}
              rules={{
                validate: async (value) => !value || (await isNominalId(value)),
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Nominal ID"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name="imageSrc"
              control={control}
              rules={{
                validate: async (value) => !value || (await isImageUrl(value)),
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  placeholder="https://example.com/image.png"
                  {...field}
                  label="Image"
                  type="url"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name="linkHref"
              control={control}
              rules={{
                validate: (value) => !value || isValidUrl(value),
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  placeholder="https://example.com"
                  {...field}
                  label="Link"
                  type="url"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {!!notice.code && (
            <Fragment>
              <Button color="error" onClick={onDelete}>
                Delete
              </Button>
              <Box sx={{ flexGrow: 1 }} />
            </Fragment>
          )}
          <Button onClick={handleClose}>Cancel</Button>
          <Button form="notice-editor-form" type="submit">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
}
